import { Card } from '@r/components/ui/card.tsx';
import type { CSSProperties } from 'react';
import { Button } from '@r/components/ui/button.tsx';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from '@r/components/ui/carousel.tsx';
import Autoplay from 'embla-carousel-autoplay';
import { useTranslation } from 'react-i18next';
import { useApiMediasIdGet } from '@r/api/generated/client.ts';
import {
  getMediaIdFromIri,
  getWidgetImageOverlayOpacity,
  resolveSlideBackgroundImageIri,
  resolveWidgetAssetUrl,
  type WidgetCtaPosition,
  type WidgetSlideContent
} from '@r/helpers/widget-workspace-slides.ts';
import { useIsMobile } from '@r/hooks/use-mobile.ts';
import { cn } from '@r/utils/utils.ts';

/**
 * Rendu participant du bloc CTA (carousel ou slide unique).
 * Source des slides : `getWidgetSlidesFromConfigurations` (client) ou aperçu admin.
 */
/** Accepte `#hex`, `rgb()`, `hsl()` ou valeurs HSL brutes stockées par l’admin (`0 64% 46%`). */
const normalizeCssColor = (input?: string | null): string | undefined => {
  if (!input) return undefined;
  const value = input.trim();

  if (/^(#|rgb\(|rgba\(|hsl\(|hsla\(|[a-zA-Z]+$)/.test(value)) {
    return value;
  }

  const hslCorePattern = /^\d+(?:\.\d+)?\s+\d+%?\s+\d+%?(?:\s*\/\s*(?:\d+%?|\d+(?:\.\d+)?))?$/;
  if (hslCorePattern.test(value)) {
    return `hsl(${value})`;
  }

  return value;
};

type WidgetSlideCardProps = {
  slide: WidgetSlideContent;
  /** `large` = rendu desktop (aperçu admin), sans réduire typo/hauteur sur viewport étroit. */
  displaySize?: 'responsive' | 'large';
};

/**
 * Une slide CTA : fond (desktop/mobile), overlay contraste (`filter_image`), texte + CTA.
 * Le média passe par GET `/medias/{id}` quand `configValue` est une IRI `/medias/…`.
 */
export const WidgetSlideCard = ({ slide, displaySize = 'responsive' }: WidgetSlideCardProps) => {
  const isLargeDisplay = displaySize === 'large';
  const isMobileViewport = useIsMobile();
  /** Aperçu admin `large` : toujours fond desktop, même sur viewport étroit. */
  const useMobileBackground = !isLargeDisplay && isMobileViewport;
  const backgroundImageIri = resolveSlideBackgroundImageIri(slide, useMobileBackground);
  const textColor = normalizeCssColor(slide.textColor) ?? '#ffffff';
  const mediaId = getMediaIdFromIri(backgroundImageIri);
  const mediaQuery = useApiMediasIdGet(mediaId);
  const backgroundImageUrl = resolveWidgetAssetUrl(
    mediaId ? mediaQuery.data?.pathUri : backgroundImageIri
  );

  const cardStyle: CSSProperties = {
    backgroundColor: backgroundImageUrl ? undefined : 'white',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat'
  };

  const overlayOpacity = getWidgetImageOverlayOpacity(slide.filterImage);

  return (
    <Card
      className={cn(
        'flex overflow-hidden relative justify-center items-center rounded-3xl border-0 shadow-none',
        isLargeDisplay ? 'min-h-[350px]' : 'min-h-[280px] sm:min-h-[350px]'
      )}
      style={cardStyle}
    >
      {backgroundImageUrl ? (
        <>
          <div
            aria-hidden="true"
            className="absolute inset-0 z-0"
            style={{
              backgroundImage: `url(${backgroundImageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          {overlayOpacity !== undefined ? (
            <div
              aria-hidden="true"
              className="absolute inset-0 z-[1]"
              style={{ backgroundColor: `rgba(0, 0, 0, ${overlayOpacity})` }}
            />
          ) : null}
        </>
      ) : null}
      <div className="relative z-10 px-6 py-8 text-center">
        {(() => {
          const ctaPosition = (slide.ctaPosition as WidgetCtaPosition | undefined) ?? 'below_text';
          const isStackedCentered =
            ctaPosition === 'below_text' || ctaPosition === 'aligned_with_text';

          const descriptionBlock = slide.description ? (
            <h3
              className={cn(
                'mx-auto font-medium',
                isStackedCentered ? 'text-center' : 'text-center sm:text-left',
                isLargeDisplay
                  ? 'max-w-[80%] text-4xl xl:text-5xl'
                  : 'text-2xl md:max-w-[80%] md:text-4xl lg:max-w-[80%] xl:text-5xl'
              )}
              style={{ color: textColor }}
            >
              {slide.description}
            </h3>
          ) : null;

          const ctaBlock = slide.textCta ? (
            <div
              className={cn(
                isStackedCentered ? 'mx-auto flex justify-center' : 'flex shrink-0 justify-center',
                isLargeDisplay ? 'max-w-[40%]' : 'md:max-w-[80%] lg:max-w-[60%] 2xl:max-w-[40%]'
              )}
            >
              <Button
                rounded="full"
                type="button"
                className="flex gap-2 items-center px-4 py-2 text-sm font-medium"
                style={{ color: textColor }}
                onClick={() => {
                  if (slide.externLink) {
                    window.open(slide.externLink, '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                {slide.textCta}
              </Button>
            </div>
          ) : null;

          if (ctaPosition === 'left_of_text') {
            return (
              <div className="flex flex-col gap-4 items-center mx-auto max-w-[90%] sm:flex-row sm:items-center sm:justify-center">
                {ctaBlock}
                {descriptionBlock}
              </div>
            );
          }

          if (ctaPosition === 'right_of_text') {
            return (
              <div className="flex flex-col gap-4 items-center mx-auto max-w-[90%] sm:flex-row sm:items-center sm:justify-center">
                {descriptionBlock}
                {ctaBlock}
              </div>
            );
          }

          if (ctaPosition === 'aligned_with_text') {
            return (
              <div className="space-y-4">
                {ctaBlock}
                {descriptionBlock}
              </div>
            );
          }

          return (
            <div className="space-y-4">
              {descriptionBlock}
              {ctaBlock}
            </div>
          );
        })()}
      </div>
    </Card>
  );
};

export type WidgetContentBlockProps = {
  slides: WidgetSlideContent[];
  /** Désactivé en aperçu admin pour laisser naviguer manuellement. */
  enableAutoplay?: boolean;
  displaySize?: WidgetSlideCardProps['displaySize'];
};

/**
 * Affiche 1 slide seule ou un carousel (autoplay 7s, boucle).
 * Home et Boutique client : un `WidgetContentBlock` par `widget_workspace` actif.
 */
export const WidgetContentBlock = ({
  slides,
  enableAutoplay = true,
  displaySize = 'responsive'
}: WidgetContentBlockProps) => {
  const { t } = useTranslation();

  if (slides.length === 0) {
    return null;
  }

  if (slides.length <= 1) {
    return (
      <div className="overflow-hidden w-full rounded-3xl">
        <WidgetSlideCard slide={slides[0] ?? { slideIndex: 1 }} displaySize={displaySize} />
      </div>
    );
  }

  const carouselNavClassName = 'top-1/2 z-20 -translate-y-1/2 bg-background shadow-md';

  return (
    <Carousel
      className="overflow-hidden relative w-full rounded-3xl"
      opts={{
        loop: true,
        watchDrag: true
      }}
      plugins={
        enableAutoplay
          ? [
              Autoplay({
                delay: 7000
              })
            ]
          : undefined
      }
    >
      <CarouselContent className="ml-0">
        {slides.map((slide) => (
          <CarouselItem key={slide.slideIndex} className="relative pl-0">
            <WidgetSlideCard slide={slide} displaySize={displaySize} />
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselPrevious
        variant="outline"
        rounded="full"
        size="icon"
        className={cn(carouselNavClassName, 'left-3')}
        aria-label={t('home.widget.carousel.previous')}
      />
      <CarouselNext
        variant="outline"
        rounded="full"
        size="icon"
        className={cn(carouselNavClassName, 'right-3')}
        aria-label={t('home.widget.carousel.next')}
      />
    </Carousel>
  );
};
